import * as Vue from 'vue'
import { inject, onBeforeUnmount, watch, type App, type Component, type InjectionKey, type Ref } from 'vue'
import { getPage, getSources, getView, isGuest, postViewCheck, type ViewDiagnostic } from '@/lib/api'
import { logRowSource, type Annotation, type Source, type ViewSignal } from '@/types'
import { MarAreaChart, MarEmptyState, MarLoadingSpinner, MarMeterRow, MarProgressBar, MarSparkLine, MarStat, MarStatusPill, MarTable, MarTag, MarUptimeBar, closeModal, openModal, type MarFuncEntry, type MarTableColumn, type ModalAnchorRect, type ModalPlacement } from '@nimling/components-nimtech'
import type * as Echarts from 'echarts'
import { domToBlob } from 'modern-screenshot'

export interface ViewState {
  rows: Ref<Record<string, unknown>[]>
  annotations: Ref<Annotation[]>
  from: Ref<string | null>
  to: Ref<string | null>
  error: Ref<string | null>
  mark?: (time: string) => void
  stateKey?: string
}

export interface ViewBuild {
  id: string
  script: string
  style: string
  scoped: boolean
  diagnostics: ViewDiagnostic[]
}

export interface ViewModule {
  id: string
  module: string
  style: string
  scoped: boolean
  diagnostics: ViewDiagnostic[]
}

export const viewKey: InjectionKey<ViewState> = Symbol('hvergel-view')

export const useView = (): ViewState => inject(viewKey) as ViewState

export const getTimeX = (time: string, width: number, from: string | null, to: string | null): number => {
  if (!from || !to) return 0
  const start = Date.parse(from)
  const span = Date.parse(to) - start
  return span > 0 ? ((Date.parse(time) - start) / span) * width : 0
}

export const getXTime = (x: number, width: number, from: string | null, to: string | null): string | null => {
  if (!from || !to || width <= 0) return null
  const start = Date.parse(from)
  return new Date(start + (x / width) * (Date.parse(to) - start)).toISOString()
}

const EDGE_SHARE = 0.08

const DAY_MS = 86_400_000

export const getTimeLabel = (value: number, from: string | null, to: string | null): string => {
  if (!from || !to) return ''
  const start = Date.parse(from)
  const span = Date.parse(to) - start
  const share = span > 0 ? (value - start) / span : 0
  if (share < EDGE_SHARE || share > 1 - EDGE_SHARE) return ''
  const at = new Date(value)
  if (span > 2 * DAY_MS) return at.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
  return `${String(at.getHours()).padStart(2, '0')}:${String(at.getMinutes()).padStart(2, '0')}`
}

export const useTimeAxis = (target: Ref<HTMLElement | null>) => {
  const view = useView()
  const onClick = (e: MouseEvent) => {
    if (!(e.ctrlKey || e.metaKey) || !target.value) return
    const box = target.value.getBoundingClientRect()
    const time = getXTime(e.clientX - box.left, box.width, view.from.value, view.to.value)
    if (time) view.mark?.(time)
  }
  watch(target, (el, old) => {
    old?.removeEventListener('click', onClick)
    el?.addEventListener('click', onClick)
  }, { immediate: true })
  onBeforeUnmount(() => target.value?.removeEventListener('click', onClick))
}

export interface ViewOpenTarget {
  at?: string | number
  from?: string
  to?: string
}

export interface ViewLogsTarget {
  source?: string
  service?: string
  severity?: string
  status?: string | number
  route?: string
  trace?: string
  at?: string | number
  line?: string
  window?: string
  from?: string
  to?: string
  query?: string
}

const OPEN_SPAN_MS = 15 * 60_000

export interface ViewNavigation {
  target: 'logs' | 'trace' | 'page' | 'view'
  id: string
  params: Record<string, string>
  path: string
}

let navigate = (to: ViewNavigation) => window.location.assign(to.path)

let preview: ((to: ViewNavigation) => void) | null = null

export const setViewNavigate = (next: (to: ViewNavigation) => void) => {
  navigate = next
}

export const setViewPreview = (next: ((to: ViewNavigation) => void) | null) => {
  preview = next
}

const getOpenParams = (entries: Record<string, string | undefined>): Record<string, string> => {
  const params: Record<string, string> = {}
  for (const [key, value] of Object.entries(entries)) if (value) params[key] = value
  return params
}

const openPath = (target: ViewNavigation['target'], id: string, base: string, entries: Record<string, string | undefined>) => {
  const params = getOpenParams(entries)
  const query = new URLSearchParams(params).toString()
  const to = { target, id, params, path: query ? `${base}?${query}` : base }
  if (preview) preview(to)
  else navigate(to)
}

const getOpenTime = (at?: string | number): number => (typeof at === 'number' ? at : at ? Date.parse(at) : NaN)

const getOpenRange = (target: ViewOpenTarget): Record<string, string | undefined> => {
  const at = getOpenTime(target.at)
  if (!Number.isFinite(at)) return getOpenParams({ from: target.from, to: target.to })
  return { from: new Date(at - OPEN_SPAN_MS).toISOString(), to: new Date(at + OPEN_SPAN_MS).toISOString() }
}

let logSources: Promise<Source[]> | null = null

const getLogSource = async (service?: string): Promise<string | null> => {
  logSources ??= getSources().then((rows) => rows.filter((s) => logRowSource(s)), () => {
    logSources = null
    return []
  })
  const rows = await logSources
  const serviceOf = (s: Source) => (s.config as { service?: string } | null)?.service ?? s.name.toLowerCase().replace(/\s+/g, '-')
  return (rows.find((s) => !!service && serviceOf(s) === service) ?? rows.find((s) => !(s.config as { service?: string } | null)?.service) ?? rows[0])?.id ?? null
}

const SPAN_UNITS: Record<string, number> = { ms: 1, s: 1_000, m: 60_000, h: 3_600_000, d: 86_400_000 }

const getSpanMs = (span?: string): number | null => {
  const parts = [...(span ?? '').matchAll(/(\d+(?:\.\d+)?)(ms|s|m|h|d)/g)]
  return parts.length ? parts.reduce((sum, [, amount, unit]) => sum + Number(amount) * SPAN_UNITS[unit], 0) : null
}

const getOpenCarry = (): Record<string, string> => {
  const carried: Record<string, string> = {}
  for (const [key, value] of new URLSearchParams(window.location.search)) {
    if (key === 'from' || key === 'to' || key.startsWith('var.')) carried[key] = value
  }
  return carried
}

const getCarriedRange = (): { from?: string; to?: string } => {
  const { from, to } = getOpenCarry()
  const now = Date.now()
  const toMs = to ? (Date.parse(to) || now) : now
  const fromMs = from ? Date.parse(from) || toMs - (getSpanMs(from) ?? OPEN_SPAN_MS) : NaN
  return Number.isFinite(fromMs) ? { from: new Date(fromMs).toISOString(), to: new Date(toMs).toISOString() } : {}
}

export const openLogs = async (target: ViewLogsTarget = {}): Promise<boolean> => {
  if (isGuest()) return false
  const source = target.source ?? (await getLogSource(target.service))
  if (!source) return false
  const time = getOpenTime(target.at)
  const span = getSpanMs(target.window) ?? OPEN_SPAN_MS
  const range = Number.isFinite(time)
    ? { from: new Date(time - span).toISOString(), to: new Date(time + span).toISOString() }
    : target.from || target.to ? { from: target.from, to: target.to } : getCarriedRange()
  const status = target.status == null ? undefined : String(target.status)
  openPath('logs', source, `/streams/${source}`, {
    service: target.service, severity: target.severity, status, route: target.route, trace: target.trace,
    ...range, at: Number.isFinite(time) ? new Date(time).toISOString() : undefined, line: target.line, q: target.query,
  })
  return true
}

export const openTrace = async (traceId: string, spanId?: string): Promise<boolean> => {
  if (isGuest() || !traceId) return false
  openPath('trace', traceId, '/telemetry', { trace: traceId, span: spanId })
  return true
}

export const openPage = async (pageId: string, target: ViewOpenTarget = {}): Promise<boolean> => {
  if (isGuest() && !(await getPage(pageId).then(() => true, () => false))) return false
  const range = target.at || target.from || target.to ? getOpenRange(target) : {}
  openPath('page', pageId, `/pages/${pageId}`, { ...getOpenCarry(), ...range })
  return true
}

export const openView = async (viewId: string, target: ViewOpenTarget = {}): Promise<boolean> => {
  if (isGuest() && !(await getView(viewId).then(() => true, () => false))) return false
  openPath('view', viewId, `/view/${viewId}`, getOpenRange(target))
  return true
}

const getBuildId = (text: string) => {
  let hash = 0
  for (let i = 0; i < text.length; i++) hash = (hash * 31 + text.charCodeAt(i)) | 0
  return (hash >>> 0).toString(36)
}

export const getViewBuild = async (component: string, style: string): Promise<ViewBuild> => {
  const { parse, compileScript, compileStyle } = await import('vue/compiler-sfc')
  const id = getBuildId(component + style)
  const build: ViewBuild = { id, script: '', style: '', scoped: false, diagnostics: [] }
  const { descriptor, errors } = parse(component, { filename: 'view.vue' })
  for (const e of errors) {
    const loc = 'loc' in e && e.loc ? e.loc.start : null
    build.diagnostics.push({ source: 'component', message: e.message, line: loc?.line ?? 0, column: loc?.column ?? 0 })
  }
  for (const s of descriptor.styles) {
    build.diagnostics.push({ source: 'component', message: 'A component carries no <style>; its CSS is the style', line: s.loc.start.line, column: 1 })
  }
  if (build.diagnostics.length) return build
  try {
    build.script = compileScript(descriptor, { id, inlineTemplate: true }).content
  } catch (err) {
    const loc = (err as { loc?: { start: { line: number; column: number } } }).loc?.start
    build.diagnostics.push({ source: 'component', message: err instanceof Error ? err.message : String(err), line: loc?.line ?? 0, column: loc?.column ?? 0 })
    return build
  }
  if (!style.trim()) return build
  const out = compileStyle({ source: style, id: `data-v-${id}`, scoped: true, filename: 'view.css' })
  for (const e of out.errors) {
    const at = e as { line?: number; column?: number }
    build.diagnostics.push({ source: 'style', message: e.message, line: at.line ?? 0, column: at.column ?? 0 })
  }
  build.scoped = true
  build.style = out.code
  return build
}

const MODULE_CACHE = 'hvergel-view-modules-v1'

const modules = new Map<string, Promise<ViewModule>>()

const getCachedModule = async (key: string): Promise<ViewModule | null> => {
  if (typeof caches === 'undefined') return null
  try {
    const hit = await (await caches.open(MODULE_CACHE)).match(`/view-module/${key}`)
    return hit ? ((await hit.json()) as ViewModule) : null
  } catch {
    return null
  }
}

const putCachedModule = async (key: string, built: ViewModule) => {
  if (typeof caches === 'undefined') return
  try {
    await (await caches.open(MODULE_CACHE)).put(`/view-module/${key}`, new Response(JSON.stringify(built), { headers: { 'Content-Type': 'application/json' } }))
  } catch {
    return
  }
}

const buildViewModule = async (key: string, component: string, style: string): Promise<ViewModule> => {
  const cached = await getCachedModule(key)
  if (cached) return cached
  const build = await getViewBuild(component, style)
  const out: ViewModule = { id: build.id, module: '', style: build.style, scoped: build.scoped, diagnostics: build.diagnostics }
  if (build.diagnostics.length) return out
  const check = await postViewCheck({ component, style, module: build.script })
  out.diagnostics = check.diagnostics.filter((d) => d.severity !== 'warning')
  out.module = check.module ?? ''
  if (!out.diagnostics.length && out.module) await putCachedModule(key, out)
  return out
}

export const getViewModule = (component: string, style: string): Promise<ViewModule> => {
  const key = getBuildId(`${component}\u0000${style}`) + component.length.toString(36) + style.length.toString(36)
  let held = modules.get(key)
  if (!held) {
    held = buildViewModule(key, component, style)
    modules.set(key, held)
    held.catch(() => modules.delete(key))
  }
  return held
}

const styleUses = new Map<string, { el: HTMLStyleElement; count: number }>()

export const holdStyle = (id: string, css: string): (() => void) => {
  const held = styleUses.get(id) ?? { el: Object.assign(document.createElement('style'), { textContent: css }), count: 0 }
  if (held.count === 0) document.head.appendChild(held.el)
  held.count++
  styleUses.set(id, held)
  return () => {
    held.count--
    if (held.count === 0) {
      held.el.remove()
      styleUses.delete(id)
    }
  }
}

export type ViewTipAnchor = HTMLElement | ModalAnchorRect | { x: number; y: number }

const TIP_PLACEMENT: ModalPlacement = { side: 'bottom', align: 'start', offset: 12, flip: true }

const openTips = new Set<string>()

let tipsBound = false

const closeTips = () => {
  for (const id of openTips) closeModal(id)
  openTips.clear()
}

const bindTips = () => {
  if (tipsBound) return
  tipsBound = true
  window.addEventListener('scroll', closeTips, { capture: true, passive: true })
  window.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeTips()
  })
}

const getTipRect = (anchor: ViewTipAnchor): HTMLElement | ModalAnchorRect =>
  anchor instanceof HTMLElement || !('x' in anchor) ? anchor : { left: anchor.x, top: anchor.y, right: anchor.x, bottom: anchor.y, width: 0, height: 0 }

let tipCount = 0

const getTipId = () => `view-tip:${(tipCount += 1)}`

const openTip = (id: string, anchor: ViewTipAnchor, text: string, placement?: ModalPlacement) => {
  bindTips()
  openTips.add(id)
  openModal({ kind: 'tooltip', id, text, anchor: () => getTipRect(anchor), placement: { ...TIP_PLACEMENT, ...placement } })
}

const closeTip = (id: string) => {
  if (!openTips.delete(id)) return
  closeModal(id)
}

export const useTooltip = () => {
  const id = getTipId()
  onBeforeUnmount(() => closeTip(id))
  return {
    open: (anchor: ViewTipAnchor, text: string, placement?: ModalPlacement) => openTip(id, anchor, text, placement),
    close: () => closeTip(id),
  }
}

const getTipText = (html: string): string => {
  const box = document.createElement('div')
  box.innerHTML = html.replace(/<br\s*\/?>/gi, '\n').replace(/<\/(div|p|li|tr)>/gi, '\n').replace(/<\/span>/gi, ' </span>')
  return (box.textContent ?? '').split('\n').map((line) => line.replace(/\s+/g, ' ').trim()).filter(Boolean).join('\n')
}

const getTipOption = (tooltip: unknown, sink: HTMLElement): unknown => {
  if (Array.isArray(tooltip)) return tooltip.map((t) => getTipOption(t, sink))
  if (!tooltip || typeof tooltip !== 'object') return tooltip
  return { ...tooltip, renderMode: 'html', appendTo: () => sink, confine: false, transitionDuration: 0 }
}

const AXIS_LABEL_PX = 72

const AXIS_Z = 10

type AxisLook = { width: number; opacity: number; backdrop: string; compact: boolean }

const getAxisLook = (chart: Echarts.ECharts): AxisLook => {
  const style = getComputedStyle(chart.getDom())
  const opacity = parseFloat(style.getPropertyValue('--view-axis-opacity'))
  return { width: chart.getWidth(), opacity: Number.isNaN(opacity) ? 1 : opacity, backdrop: style.getPropertyValue('--view-axis-backdrop').trim() || 'transparent', compact: parseFloat(style.getPropertyValue('--view-compact')) > 0 }
}

const getAxisOption = (axis: unknown, look: AxisLook): unknown => {
  if (Array.isArray(axis)) return axis.map((one) => getAxisOption(one, look))
  if (!axis || typeof axis !== 'object') return axis
  const own = look.compact ? { ...(axis as Record<string, unknown>), name: '' } : (axis as Record<string, unknown>)
  const label = (own.axisLabel ?? {}) as Record<string, unknown>
  const backdrop = label.inside ? { backgroundColor: look.backdrop, padding: [1, 4], borderRadius: 3 } : {}
  if (own.type !== 'time') return { ...own, z: AXIS_Z, axisLabel: { hideOverlap: true, ...backdrop, ...label, opacity: look.opacity } }
  const min = typeof own.min === 'number' ? new Date(own.min).toISOString() : null
  const max = typeof own.max === 'number' ? new Date(own.max).toISOString() : null
  const formatter = min && max ? (value: number) => getTimeLabel(value, min, max) : undefined
  return { splitNumber: Math.max(2, Math.floor(look.width / AXIS_LABEL_PX)), ...own, z: AXIS_Z, axisLabel: { hideOverlap: true, formatter, ...backdrop, ...label, opacity: look.opacity } }
}

const getLegendOption = (legend: unknown, look: AxisLook): unknown => {
  if (Array.isArray(legend)) return legend.map((one) => getLegendOption(one, look))
  if (!legend || typeof legend !== 'object') return legend
  return { show: true, ...legend, ...(look.compact ? { show: false } : {}) }
}

export const getViewCharts = (lib: typeof Echarts): typeof Echarts => ({
  ...lib,
  init: (...args: Parameters<typeof Echarts.init>) => {
    const chart = lib.init(...args)
    const id = getTipId()
    const sink = document.createElement('div')
    let point = { x: 0, y: 0 }
    chart.getZr().on('mousemove', (e) => {
      const event = e.event as MouseEvent | undefined
      if (event) point = { x: event.clientX, y: event.clientY }
    })
    const sync = () => {
      const el = sink.lastElementChild as HTMLElement | null
      const shown = !!el && el.style.visibility !== 'hidden' && el.style.opacity !== '0'
      const text = shown ? getTipText(el.innerHTML) : ''
      if (text) openTip(id, { ...point }, text)
      else closeTip(id)
    }
    const watcher = new MutationObserver(sync)
    watcher.observe(sink, { subtree: true, childList: true, attributes: true, characterData: true })
    chart.on('globalout', () => closeTip(id))
    const setOption = chart.setOption.bind(chart) as (option: Record<string, unknown>, ...rest: unknown[]) => void
    let axes: { xAxis?: unknown; yAxis?: unknown; legend?: unknown } = {}
    const getAxes = (option: Record<string, unknown>) => {
      const look = getAxisLook(chart)
      return {
        ...('xAxis' in option ? { xAxis: getAxisOption(option.xAxis, look) } : {}),
        ...('yAxis' in option ? { yAxis: getAxisOption(option.yAxis, look) } : {}),
        ...('legend' in option ? { legend: getLegendOption(option.legend, look) } : {}),
      }
    }
    const paintAxes = () => {
      if (axes.xAxis !== undefined || axes.yAxis !== undefined || axes.legend !== undefined) setOption(getAxes(axes as Record<string, unknown>))
    }
    let fading = 0
    const fade = () => {
      paintAxes()
      fading = requestAnimationFrame(fade)
    }
    const watchFade = (e: TransitionEvent) => {
      if (e.target !== chart.getDom() || e.propertyName !== '--view-axis-opacity') return
      cancelAnimationFrame(fading)
      if (e.type === 'transitionrun') fading = requestAnimationFrame(fade)
      else paintAxes()
    }
    for (const type of ['transitionrun', 'transitionend', 'transitioncancel'] as const) chart.getDom().addEventListener(type, watchFade)
    chart.setOption = ((option: Record<string, unknown>, ...rest: unknown[]) => {
      if (!option) return setOption(option, ...rest)
      if ('xAxis' in option) axes = { ...axes, xAxis: option.xAxis }
      if ('yAxis' in option) axes = { ...axes, yAxis: option.yAxis }
      if ('legend' in option) axes = { ...axes, legend: option.legend }
      setOption({ ...option, ...getAxes(option), ...('tooltip' in option ? { tooltip: getTipOption(option.tooltip, sink) } : {}) }, ...rest)
    }) as typeof chart.setOption
    const resize = chart.resize.bind(chart)
    chart.resize = ((...args: Parameters<typeof chart.resize>) => {
      if (resizing) {
        heldCharts.add(chart)
        return
      }
      heldCharts.delete(chart)
      resize(...args)
      paintAxes()
    }) as typeof chart.resize
    const release = putViewResize(chart.getDom(), chart)
    const dispose = chart.dispose.bind(chart)
    chart.dispose = () => {
      release()
      heldCharts.delete(chart)
      cancelAnimationFrame(fading)
      for (const type of ['transitionrun', 'transitionend', 'transitioncancel'] as const) chart.getDom().removeEventListener(type, watchFade)
      watcher.disconnect()
      closeTip(id)
      dispose()
    }
    return chart
  },
})

const KIT = { MarStat, MarMeterRow, MarProgressBar, MarSparkLine, MarStatusPill, MarTag, MarEmptyState, MarLoadingSpinner, MarTable, MarAreaChart, MarUptimeBar }

const VIEW_MINIMUM = { chart: { width: 20, height: 10 }, figure: { width: 10, height: 5 } }

const CHART_MARK = /echarts|uPlot|MarTable|MarAreaChart/

const getStyleEm = (style: string, name: string): number | null => {
  const found = style.match(new RegExp(`${name}\\s*:\\s*([\\d.]+)\\s*r?em`))
  return found ? parseFloat(found[1]) : null
}

export const getViewMinimum = (view: { component?: string | null; style?: string | null }): { width: number; height: number } => {
  const kind = CHART_MARK.test(view.component ?? '') ? VIEW_MINIMUM.chart : VIEW_MINIMUM.figure
  const style = view.style ?? ''
  return { width: getStyleEm(style, '--view-min-width') ?? kind.width, height: getStyleEm(style, '--view-min-height') ?? kind.height }
}

const COMPACT_STEPS: [number, string][] = [[1e9, 'B'], [1e6, 'M'], [1e3, 'k']]

export const formatNumber = (value: number): string => {
  const size = Math.abs(value)
  for (const [step, suffix] of COMPACT_STEPS) if (size >= step) return `${+(value / step).toFixed(size >= step * 10 ? 0 : 1)}${suffix}`
  return String(+value.toFixed(size >= 10 ? 0 : 2))
}

const DURATION_STEPS: [number, string][] = [[86_400_000, 'd'], [3_600_000, 'h'], [60_000, 'min'], [1000, 's']]

export const formatDuration = (ms: number): string => {
  for (const [step, suffix] of DURATION_STEPS) if (Math.abs(ms) >= step) return `${+(ms / step).toFixed(ms / step >= 10 ? 0 : 1)} ${suffix}`
  return `${+ms.toFixed(ms >= 10 ? 0 : 1)} ms`
}

const BYTE_STEPS = ['B', 'KB', 'MB', 'GB', 'TB']

export const formatBytes = (bytes: number): string => {
  let size = Math.abs(bytes)
  let unit = 0
  while (size >= 1024 && unit < BYTE_STEPS.length - 1) {
    size /= 1024
    unit++
  }
  return `${Math.sign(bytes) < 0 ? '-' : ''}${+size.toFixed(size >= 10 || unit === 0 ? 0 : 1)} ${BYTE_STEPS[unit]}`
}

export const formatPercent = (share: number): string => `${+share.toFixed(Math.abs(share) >= 10 ? 0 : 1)} %`

export const formatTime = (time: string | number): string => {
  const at = new Date(time)
  return Date.now() - at.getTime() > 86_400_000
    ? at.toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
    : at.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' })
}

export interface ViewTableState {
  order?: string[]
  widths?: Record<string, number>
  hidden?: string[]
  sort?: { key: string | null; asc: boolean }
}

export interface ViewTableStore {
  get: (key: string) => ViewTableState | undefined
  put: (key: string, state: ViewTableState) => void
}

let tableStore: ViewTableStore = {
  get: () => undefined,
  put: () => undefined,
}

export const setViewTableStore = (store: ViewTableStore) => {
  tableStore = store
}

const getSorted = (rows: Record<string, unknown>[], sort: ViewTableState['sort']) => {
  if (!sort?.key) return rows
  const key = sort.key
  const dir = sort.asc ? 1 : -1
  return [...rows].sort((a, b) => {
    const x = a[key]
    const y = b[key]
    if (typeof x === 'number' && typeof y === 'number') return (x - y) * dir
    return String(x ?? '').localeCompare(String(y ?? '')) * dir
  })
}

export const useTableState = (name: string, columns: Ref<MarTableColumn[]> | MarTableColumn[], rows?: Ref<Record<string, unknown>[]>) => {
  const view = useView()
  const key = `${view.stateKey ?? 'view'}:${name}`
  const state = Vue.ref<ViewTableState>(tableStore.get(key) ?? {})
  const source = Vue.computed(() => (Vue.isRef(columns) ? columns.value : columns))
  const put = (patch: ViewTableState) => {
    state.value = { ...state.value, ...patch }
    tableStore.put(key, state.value)
  }
  const ordered = Vue.computed(() => {
    const hidden = new Set(state.value.hidden ?? [])
    const order = state.value.order ?? []
    const rank = (c: MarTableColumn) => {
      const at = order.indexOf(c.key)
      return at < 0 ? order.length + source.value.indexOf(c) : at
    }
    return source.value.filter((c) => !hidden.has(c.key)).sort((a, b) => rank(a) - rank(b))
  })
  return Vue.reactive({
    columns: ordered,
    rows: Vue.computed(() => getSorted(rows?.value ?? view.rows.value, state.value.sort)),
    columnWidth: Vue.computed(() => Object.fromEntries(Object.entries(state.value.widths ?? {}).map(([k, w]) => [k, `${w}px`]))),
    sortKey: Vue.computed(() => state.value.sort?.key ?? null),
    sortDir: Vue.computed(() => (state.value.sort?.asc === false ? 'desc' : 'asc')),
    variant: 'card',
    manualSort: true,
    resizableColumns: true,
    'onUpdate:columns': (next: MarTableColumn[]) => put({ order: next.map((c) => c.key) }),
    'onUpdate:columnWidth': (change: { key: string; width: number }) => put({ widths: { ...state.value.widths, [change.key]: Math.round(change.width) } }),
    'onSort-change': (sort: { key: string | null; asc: boolean }) => put({ sort }),
  })
}

const harness = { ...KIT, useView, useTimeAxis, useTooltip, useTableState, getTimeX, getXTime, getTimeLabel, formatNumber, formatDuration, formatBytes, formatPercent, formatTime, openLogs, openTrace, openPage, openView }

const components = new Map<string, Component & { __scopeId?: string }>()

export const getViewComponent = (built: ViewModule): Component & { __scopeId?: string } => {
  const held = components.get(built.module)
  if (held) return held
  const imports: Record<string, unknown> = { vue: Vue, hvergel: harness }
  const scope = { exports: {} as Record<string, unknown> }
  new Function('require', 'module', 'exports', built.module)((name: string) => imports[name], scope, scope.exports)
  const component = (scope.exports.default ?? scope.exports) as Component & { __scopeId?: string }
  if (built.scoped) component.__scopeId = `data-v-${built.id}`
  components.set(built.module, component)
  return component
}

export const putViewKit = (app: App) => {
  for (const [name, part] of Object.entries(KIT)) app.component(name, part)
}

const RESIZE_SETTLE_MS = 150

let resizing = false
let settleTimer: number | null = null
const heldCharts = new Set<Echarts.ECharts>()
const viewSizes = new WeakMap<Element, string>()
const viewCharts = new WeakMap<Element, Echarts.ECharts>()

export const putResizing = () => {
  if (!resizing) document.documentElement.classList.add('view-resizing')
  resizing = true
  if (settleTimer != null) window.clearTimeout(settleTimer)
  settleTimer = window.setTimeout(() => {
    settleTimer = null
    resizing = false
    document.documentElement.classList.remove('view-resizing')
    for (const chart of [...heldCharts]) chart.resize()
  }, RESIZE_SETTLE_MS)
}

let viewSizing: ResizeObserver | null = null

export const putViewResize = (el: HTMLElement, chart?: Echarts.ECharts): (() => void) => {
  viewSizing ??= new ResizeObserver((entries) => {
    let moved = false
    for (const entry of entries) {
      const size = `${Math.round(entry.contentRect.width)}x${Math.round(entry.contentRect.height)}`
      if (viewSizes.has(entry.target) && viewSizes.get(entry.target) !== size) {
        moved = true
        const held = viewCharts.get(entry.target)
        if (held) heldCharts.add(held)
      }
      viewSizes.set(entry.target, size)
    }
    if (moved) putResizing()
  })
  if (chart) {
    viewCharts.set(el, chart)
    viewSizes.set(el, `${Math.round(chart.getWidth())}x${Math.round(chart.getHeight())}`)
  }
  viewSizing.observe(el)
  return () => {
    viewSizing?.unobserve(el)
    viewSizes.delete(el)
    viewCharts.delete(el)
  }
}

export const getTemplateTitle = (name: string): string => name.split(' · ')[0]

export const getSignalTone = (signal: ViewSignal): 'info' | 'success' | 'warn' | 'secondary' =>
  signal === 'logs' ? 'info' : signal === 'metrics' ? 'success' : signal === 'traces' ? 'warn' : 'secondary'

export const fieldNotes: Record<string, string> = {
  id: 'Stable id of the row; use it as the key when the component lists rows one by one',
  timestamp: 'When the row was read or stored, an RFC3339 string; prefer time or bucket when the row carries one',
  time: 'When the record happened, an RFC3339 string; parse it with times.parse to bucket rows over the range',
  end_time: 'When the span finished; time to end_time is the span drawn on a timeline',
  bucket: 'Start of the time bucket the row sums up, an RFC3339 string; one point of a chart per bucket',
  service: 'service.name of the sender; the usual field to group, colour or filter by',
  environment: 'deployment.environment of the sender, such as tst or prd; filter on it to split stages',
  severity_number: 'OTLP severity from 1 to 24: 9 is info, 13 warn, 17 error and 21 fatal; compare the number, the text varies by sender',
  severity_text: 'The level as the sender wrote it, such as INFO or Error; show it, but filter on severity_number',
  body: 'The log message itself; count by it for top messages or show it in a log list',
  trace_id: 'Id of the trace the record belongs to; group spans by it to rebuild a whole request',
  span_id: 'Id of this span; children name it as parent_span_id',
  parent_span_id: 'Span this one was called from, empty for a root; follow it to draw a waterfall or a service map',
  attributes: 'Map of the record attributes; read a key with r.attributes["http.route"] and split or filter by it',
  resource: 'Map of the sender resource, such as host.name; read a key with r.resource["host.name"]',
  name: 'Metric name, or the operation name of a span; filter on it before you aggregate',
  kind: 'Metric kind (gauge, sum, histogram) or span kind (server, client, internal); server spans are incoming requests',
  value: 'The number of the point: a metric sample, or the evaluated value of an mql series',
  avg: 'Mean of the samples inside the bucket; the line most metric charts draw',
  min: 'Smallest sample inside the bucket; with max it draws a band around avg',
  max: 'Largest sample inside the bucket; peaks hide in avg, show max for spikes',
  count: 'How many samples or records the row sums up; in lql stats rows it is count()',
  series: 'The label set of an mql series as text; group points by it to draw one line each',
  labels: 'The labels of an mql series as a map; read one with r.labels.service for a short legend name',
  status_code: 'Span status: ok, error or unset; error spans drive error rates',
  status_message: 'Error text the span carried, empty when it succeeded',
  duration_ms: 'How long the span took in milliseconds; fold with percentile for p95 latency',
  depth: 'How deep the span sits below the root, zero for a root span',
  trace_duration_ms: 'Length of the whole trace in milliseconds, root to last span',
  span_count: 'How many spans the trace holds',
  error_count: 'How many spans of the trace failed; above zero means the request had an error',
  services: 'Every service the trace passed through, as a list',
  retention_class: 'Retention class the row is stored under; rarely worth showing',
  sa_owner_id: 'Organization that owns the row; rows are already limited to yours',
  sa_root_id: 'Root organization of the owner',
}

export const helperNotes: Record<string, string> = {
  sql: 'Runs one guarded SELECT over log_record, metric_sample, its rollups and span under your organizations and hands back rows as maps. :from, :to and :step are the page range and bucket width; pass other values as :name in the params map. The usual way a view reads the store',
  query: 'Starts a fluent read of one store table over the page range, limited to what your claims reach: chain where, over and limit, then end with rows, count or sum. Simpler than sql for a plain filter',
  catalogue: 'Runs a query the server keeps by name, with your grant and the page range, so the same read lives in one place',
  sum: 'Adds up a list of numbers: collect the values in a loop, then fold them here for a total per group',
  avg: 'Mean of a list of numbers, the usual figure for a gauge such as CPU; skewed by outliers, so prefer median for latency',
  median: 'Middle value of a list; a latency figure an outlier cannot drag',
  most: 'Largest value of a list: the peak, the slowest request, the top of an axis',
  least: 'Smallest value of a list: the floor of an axis or the fastest request',
  percentile: 'Value the given share of the list stays under: percentile(durations, 95) is the p95 latency. The share is a percentage, never 0.95',
  share: 'What percentage the part is of the whole: share(errors, total) is an error rate from 0 to 100',
  growth: 'Percentage change from the first figure to the second: growth(before, after), positive for a rise; the delta under a counter',
  round: 'Rounds to a number of decimals so the component gets tidy figures: round(avg_ms, 1)',
  months: 'Labels of the last n months, oldest first, for the x axis of a monthly chart',
  text: 'Bound in every data body, no import: string helpers such as has_prefix, contains, split and to_lower, to filter by a name or cut a route into parts',
  math: 'Bound in every data body, no import: floor, ceil, abs, pow, sqrt and log, to scale values or build logarithmic buckets',
  times: 'Bound in every data body, no import: time parsing and arithmetic, times.parse(layout, r.time) reads a row time and times.time_unix_nano turns it into a number to bucket by',
  fmt: 'Bound in every data body, no import: fmt.sprintf builds a label from numbers with a fixed format, such as two decimals',
}

export const dataSnippets: MarFuncEntry[] = [
  { label: 'count over time', token: 'res := sql(`SELECT (date_part(\'epoch\', date_bin(:step, time, :from)) * 1000)::int8 AS t, count(*)::int8 AS value FROM log_record WHERE time >= :from AND time < :to GROUP BY 1 ORDER BY 1`, {})\nreturn res\n', meta: 'logs', description: 'Counts log records per time bucket of the page step, t in epoch milliseconds: the shape bar and line views draw. Swap log_record for span to count spans' },
  { label: 'series over time', token: 'res := sql(`SELECT (date_part(\'epoch\', date_bin(:step, time, :from)) * 1000)::int8 AS t, service AS series, count(*)::int8 AS value FROM log_record WHERE time >= :from AND time < :to GROUP BY 1, 2 ORDER BY 1`, {})\nreturn res\n', meta: 'logs', description: 'One series per service bucketed over the range, the t, series and value rows trend lines, stacked areas and heatmaps draw' },
  { label: 'metric average', token: 'METRIC := "system.cpu.utilization"\nres := sql(`SELECT (date_part(\'epoch\', date_bin(:step, time, :from)) * 1000)::int8 AS t, service AS series, avg(value)::float8 AS value FROM metric_sample WHERE name = :metric AND time >= :from AND time < :to GROUP BY 1, 2 ORDER BY 1`, {metric: METRIC})\nreturn res\n', meta: 'metrics', description: 'Averages one metric per bucket and service; change METRIC to the name to draw. For long ranges read metric_sample_1h instead' },
  { label: 'counter increase', token: 'METRIC := "http.server.request.count"\nres := sql(`SELECT (date_part(\'epoch\', date_bin(:step, time, :from)) * 1000)::int8 AS t, (CASE WHEN bool_or(attributes->>\'temporality\' = \'delta\') THEN sum(value) ELSE max(value) - min(value) END)::float8 AS value FROM metric_sample WHERE name = :metric AND time >= :from AND time < :to GROUP BY 1 ORDER BY 1`, {metric: METRIC})\nreturn res\n', meta: 'metrics', description: 'A cumulative sum is a running total, so the increase per bucket is its max minus its min; a delta sum holds each export\'s increase, so it is their sum' },
  { label: 'p95 by operation', token: 'res := sql(`SELECT name AS label, round(percentile_cont(0.95) WITHIN GROUP (ORDER BY duration_ms)::numeric, 1)::float8 AS value FROM span WHERE time >= :from AND time < :to GROUP BY 1 ORDER BY 2 DESC LIMIT 10`, {})\nreturn res\n', meta: 'traces', description: 'Ranks operations by their 95th percentile duration, the label and value rows top lists draw' },
  { label: 'count by field', token: 'res := sql(`SELECT severity_text AS label, count(*)::int8 AS value FROM log_record WHERE time >= :from AND time < :to GROUP BY 1 ORDER BY 2 DESC`, {})\nreturn res\n', meta: 'logs', description: 'Counts records per value of one field into label and value rows, the shape top lists, doughnuts, strips and treemaps draw' },
  { label: 'page variable', token: 'SERVICE := text.join(is_undefined(variables.service) ? [] : variables.service, ",")\nres := sql(`SELECT body FROM log_record WHERE time >= :from AND time < :to AND (:service = \'\' OR service = ANY(string_to_array(:service, \',\'))) ORDER BY time DESC LIMIT 100`, {service: SERVICE})\nreturn res\n', meta: 'variables', description: 'Filters by the service page variable when the reader picked one: the list goes to sql as text and is split there' },
  { label: 'fluent read', token: 'return query("log_record").where("service", "api").limit(50).rows()\n', meta: 'query', description: 'A plain filtered read without sql: where, not, over and under filter, limit caps, rows, count, sum and avg end it' },
  { label: 'time in milliseconds', token: 'ms := func(s) {\n  return times.time_unix_nano(times.parse("2006-01-02T15:04:05.999999999Z07:00", s)) / 1000000\n}\n', meta: 'helper', description: 'Defines ms(text), which turns from, to or a row time into epoch milliseconds, the t every chart in the library reads' },
  { label: 'sort descending', token: 'for i := 1; i < len(out); i++ {\n  v := out[i]\n  j := i - 1\n  for j >= 0 && out[j].value < v.value {\n    out[j + 1] = out[j]\n    j--\n  }\n  out[j + 1] = v\n}\n', meta: 'pattern', description: 'Orders out by value, largest first, when the order cannot come from the sql; flip < to > for ascending' },
  { label: 'severity level', token: 'level := func(n) {\n  if n >= 17 {\n    return "error"\n  }\n  if n >= 13 {\n    return "warn"\n  }\n  if n >= 9 {\n    return "info"\n  }\n  return "debug"\n}\n', meta: 'helper', description: 'Turns an OTLP severity_number into error, warn, info or debug, so logs from senders that spell their levels differently group together' },
]

export const componentApi: MarFuncEntry[] = [
  { label: 'imports', token: "import { computed, ref } from 'vue'\nimport { getTimeX, openLogs, useTimeAxis, useView } from 'hvergel'\n", meta: 'script', description: 'The only two modules a component may import: vue for reactivity and hvergel for the harness, useView, useTimeAxis, useTooltip, getTimeX, getXTime and the click navigation openLogs, openTrace, openPage and openView. Any other import is refused by the check' },
  { label: 'useView()', token: 'const { rows, annotations, from, to, error } = useView()\n', meta: 'refs', description: 'Reads what the view shows: rows is the result of the data, annotations the markers on its placement, from and to the range as ISO text, error the last failure. All are refs: .value in script, bare in the template' },
  { label: 'useTimeAxis(plot)', token: 'const plot = ref(null)\nuseTimeAxis(plot)\n', meta: 'axis', description: 'Makes the element in plot a time axis: a Ctrl or Cmd click on it asks the page to add an annotation at that moment. Use it on the element whose width spans from to to' },
  { label: 'useTooltip()', token: 'const tip = useTooltip()\n', meta: 'overlay', description: 'A tooltip on the overlay layer of the app, never inside the card, so no tile edge cuts it: tip.open(el, text) on pointerenter or focus, or tip.open({ x: e.clientX, y: e.clientY }, text) at the pointer, and tip.close() on pointerleave or blur. It flips at the viewport edges and closes on scroll and Escape; echarts tooltips go to the same layer by themselves' },
  { label: 'getTimeX()', token: 'getTimeX(a.time, 100, from.value, to.value)', meta: 'number', description: 'Where a time falls along the axis, as a share of the width you pass. With 100 it is a CSS percentage, which places annotation marks and events in line with the plot' },
  { label: 'getXTime()', token: 'getXTime(x, 100, from.value, to.value)', meta: 'time', description: 'The ISO time at a position of the axis, the reverse of getTimeX; tick labels at fixed percentages come from it' },
  { label: 'getTimeLabel()', token: 'formatter: (v) => getTimeLabel(v, from.value, to.value)', meta: 'axis', description: 'The label of an echarts time axis tick: HH:mm, or the day for ranges over two days, and empty near either edge so no label is cut or collides; set it as axisLabel.formatter with showMinLabel and showMaxLabel false' },
  { label: 'formatNumber()', token: 'formatNumber(r.value)', meta: 'format', description: 'A count as 1.3k or 3.2M, imported from hvergel; formatDuration(ms), formatBytes(n), formatPercent(share) and formatTime(iso) format the other figures a view shows' },
  { label: '<MarStat>', token: '<MarStat :value="formatNumber(r.value)" :label="r.label" />', meta: 'kit', description: 'The view kit, in every view without an import: a figure over an uppercase label, for a row of KPIs' },
  { label: '<MarMeterRow>', token: '<MarMeterRow :label="r.label" :value="r.value" :max="r.max" unit=" %" />', meta: 'kit', description: 'The view kit: a labelled bar on a track, judged against target when one is given' },
  { label: '<MarStatusPill>', token: '<MarStatusPill :label="r.state" :severity="r.state === \'firing\' ? \'danger\' : \'success\'" />', meta: 'kit', description: 'The view kit: a state such as firing or ok, tinted by severity' },
  { label: '<MarSparkLine>', token: '<MarSparkLine :values="rows.map((r) => r.value)" filled />', meta: 'kit', description: 'The view kit: a slim trend inside a stat or a row' },
  { label: '<MarTable>', token: '<MarTable name="rows" :rows="rows" :columns="[{ key: \'name\', label: \'Name\', sortable: true }]" sticky-header />', meta: 'kit', description: 'The view kit: a full table with sortable columns, a sticky header and its own scroll; use it rather than hand written rows' },
  { label: 'openLogs()', token: "openLogs({ service: r.service, severity: r.severity, at: r.t })", meta: 'click', description: 'Opens the log stream filtered to the clicked set, any of service, severity, status, route and trace, scrolled to and marking the record nearest at (a time or epoch ms) or the record id in line, window either side of at (15m by default) or else the page range; source picks a source id, else the log source of the service. Does nothing for a guest' },
  { label: 'openTrace()', token: 'openTrace(r.trace_id, r.span_id)', meta: 'click', description: 'Opens the trace with its waterfall, the span marked when given. Call it from a click on a span or a trace row. Does nothing for a guest' },
  { label: 'openPage()', token: "openPage('<page id>', { at: r.t })", meta: 'click', description: 'Opens another page with the page range and variables, its range centred on at when given. A page id written in the code must name an existing page shared through a claim, or the save is refused; the Pages section lists them. A reader without the page lands on its 403 with Request access' },
  { label: 'openView()', token: 'openView(viewId, { at: r.time })', meta: 'click', description: 'Opens one view alone, its range centred on at or set by from and to. A guest reaches only a view its link can read' },
  { label: 'annotation fields', token: 'a.time, a.end_time, a.title', meta: 'shape', description: 'Each annotation has id, time, an optional end_time that makes it a span, title for the hover text, text and tags. Views drawn over time mark every one of them' },
  { label: 'window.echarts', token: 'window.echarts.init(plot.value)', meta: 'global', description: 'Apache ECharts, already loaded in the view frame: init on a template ref in onMounted, setOption in a render function, dispose in onBeforeUnmount; the harness resizes the chart when its box changes, so never listen to window resize or call resize' },
]

export const componentBlocks: MarFuncEntry[] = [
  { label: 'empty component', token: "<script setup>\nimport { useView } from 'hvergel'\n\nconst { rows, error } = useView()\n</script>\n\n<template>\n  <div class=\"view\">\n    <span v-if=\"error\" class=\"error\">{{ error }}</span>\n    <div v-for=\"(r, i) in rows\" :key=\"r.id ?? i\" class=\"row\">{{ r }}</div>\n  </div>\n</template>\n", meta: 'sfc', description: 'A whole component that lists every row as it comes from the data; drop it into an empty editor and replace the row markup with what the view should draw' },
  { label: 'rows loop', token: '<div v-for="(r, i) in rows" :key="r.id ?? i" class="row">{{ r.label }}</div>', meta: 'template', description: 'Repeats an element per row of the result; the key lets Vue keep rows that did not change. Swap r.label for the field to show' },
  { label: 'error line', token: '<span v-if="error" class="error">{{ error }}</span>', meta: 'template', description: 'Shows the source or data error in place of a silent empty view; every library view starts with it' },
  { label: 'compact number', token: "const compact = (v) => {\n  const a = Math.abs(v)\n  if (a >= 1e6) return +(v / 1e6).toFixed(a >= 1e7 ? 0 : 1) + 'M'\n  if (a >= 1e3) return +(v / 1e3).toFixed(a >= 1e4 ? 0 : 1) + 'k'\n  return String(+v.toFixed(a >= 10 ? 0 : 2))\n}\n", meta: 'script', description: 'Formats 15320 as 15k and 2.4e6 as 2.4M, one decimal only below ten, so figures keep a steady width in tiles and axes' },
  { label: 'peak of rows', token: 'const peak = computed(() => Math.max(0, ...rows.value.map((r) => r.value)) || 1)\n', meta: 'script', description: 'The largest value of the result, never zero, to scale bars and sparklines as a share of the biggest one' },
  { label: 'ranked bar', token: '<div v-for="r in rows" :key="r.label" class="bar">\n  <span class="name">{{ r.label }}</span>\n  <span class="track"><span class="fill" :style="{ inlineSize: (r.value / peak) * 100 + \'%\' }"></span></span>\n  <span class="value">{{ r.value }}</span>\n</div>\n', meta: 'template', description: 'One horizontal bar per row, its length a share of the peak; pair it with peak of rows and the ranked grid style' },
  { label: 'stat figure', token: '<div class="stat">\n  <span class="label">{{ rows[0]?.label }}</span>\n  <span class="value">{{ rows[0]?.value }}</span>\n</div>\n', meta: 'template', description: 'A small uppercase label over one large figure from the first row, the counter at the top of a page' },
  { label: 'svg sparkline', token: '<svg class="spark" viewBox="0 0 100 100" preserveAspectRatio="none">\n  <polyline :points="rows.map((r, i) => (i / Math.max(rows.length - 1, 1)) * 100 + \',\' + (100 - (r.value / peak) * 100)).join(\' \')" />\n</svg>\n', meta: 'template', description: 'A line without axes across the full width, stretched to any size; give the polyline vector-effect non-scaling-stroke in the style so it stays one stroke wide' },
  { label: 'annotation marks', token: "const marks = computed(() => annotations.value.map((a) => {\n  const left = Math.min(100, Math.max(0, getTimeX(a.time, 100, from.value, to.value)))\n  const end = a.end_time ? Math.min(100, Math.max(0, getTimeX(a.end_time, 100, from.value, to.value))) : left\n  return { id: a.id, title: a.title, left, width: end - left }\n}))\n", meta: 'script', description: 'Places every annotation of the placement as a percentage along the time axis, a point or a span; draw them with the marks overlay in the template' },
  { label: 'marks overlay', token: '<span v-for="m in marks" :key="m.id" :class="m.width > 0 ? \'span\' : \'mark\'" :style="{ left: m.left + \'%\', width: m.width > 0 ? m.width + \'%\' : undefined }" :title="m.title"></span>', meta: 'template', description: 'Draws the annotation marks over a plot that spans from to to: a dashed line for a moment, a tinted band for a span. Put it inside the positioned frame of the plot' },
  { label: 'echarts chart', token: "<script setup>\nimport { onBeforeUnmount, onMounted, ref, watch } from 'vue'\nimport { useTimeAxis, useView } from 'hvergel'\n\nconst { rows, from, to, error } = useView()\nconst plot = ref(null)\nlet chart = null\nuseTimeAxis(plot)\n\nconst tone = (name) => getComputedStyle(plot.value).getPropertyValue(name).trim()\n\nconst render = () => {\n  if (!chart) return\n  const fg = tone('--mar-text-muted-color')\n  chart.setOption({\n    animation: false,\n    color: [tone('--mar-primary-color'), tone('--mar-info-color'), tone('--mar-success-color')],\n    textStyle: { color: fg },\n    grid: { left: 0, right: 0, top: '10%', bottom: '10%' },\n    tooltip: { trigger: 'axis' },\n    xAxis: { type: 'time', min: from.value ? Date.parse(from.value) : undefined, max: to.value ? Date.parse(to.value) : undefined, axisLabel: { color: fg } },\n    yAxis: { type: 'value', axisLabel: { color: fg, inside: true }, splitLine: { lineStyle: { opacity: 0.15 } } },\n    series: [{ type: 'line', showSymbol: false, data: rows.value.map((r) => [r.t, r.value]) }]\n  }, true)\n}\n\nonMounted(() => {\n  chart = window.echarts.init(plot.value)\n  render()\n})\n\nwatch([rows, from, to], render)\n\nonBeforeUnmount(() => {\n  if (chart) chart.dispose()\n})\n</script>\n\n<template>\n  <div class=\"view\">\n    <span v-if=\"error\" class=\"error\">{{ error }}</span>\n    <div ref=\"plot\" class=\"plot\"></div>\n  </div>\n</template>\n", meta: 'sfc', description: 'A whole ECharts line chart over time with the theme colours read from the style, redrawn when rows or the range change and disposed with the view. Change the series type to bar or scatter for other charts. Its axis labels paint above the series and clear on hover by themselves, so never set axis z, label opacity or a label background' },
]

const styleToken = (name: string, description: string): MarFuncEntry => ({ label: name, token: `var(${name})`, meta: 'colour', description })

export const styleTokens: MarFuncEntry[] = [
  styleToken('--mar-primary-color', 'The accent: the one series or figure the eye should land on first'),
  styleToken('--mar-info-color', 'Calm blue for neutral counts and the second series'),
  styleToken('--mar-success-color', 'Green for healthy states, good deltas and the low end of a meter'),
  styleToken('--mar-warn-color', 'Amber for warnings, warm meters and annotation marks'),
  styleToken('--mar-danger-color', 'Red for errors, failed spans, hot meters and error text'),
  styleToken('--mar-purple-500', 'Violet, a fifth series colour that reads apart from the status colours'),
  styleToken('--mar-teal-500', 'Teal, a sixth series colour'),
  styleToken('--mar-text-color', 'Body text in the current theme; figures and names'),
  styleToken('--mar-text-muted-color', 'Quieter text for labels, units, axes and notes'),
  styleToken('--mar-surface-0', 'The card surface the view sits on; use it for halos and sticky headers'),
  styleToken('--mar-surface-100', 'A step off the surface, for alternating rows'),
  styleToken('--mar-surface-200', 'Tracks behind bars and meters'),
  styleToken('--mar-content-border-color', 'Hairlines between rows and around tables'),
  { label: '--mar-font-mono', token: 'var(--mar-font-mono)', meta: 'font', description: 'Monospace for figures, times and ids, so digits line up' },
  { label: '--mar-font-sans', token: 'var(--mar-font-sans)', meta: 'font', description: 'The interface font for labels and prose' },
  { label: '--glass-surface', token: 'var(--glass-surface)', meta: 'glass', description: 'Translucent fill of the glass cards; a tile inside a view on it blends with the page' },
  { label: '--glass-border', token: 'var(--glass-border)', meta: 'glass', description: 'The edge of glass cards, a hairline that holds on both themes' },
  { label: '--glass-surface-raised', token: 'var(--glass-surface-raised)', meta: 'glass', description: 'A brighter glass for an element that must stand above the card' },
]

export const styleLayouts: MarFuncEntry[] = [
  { label: 'tint', token: 'color-mix(in srgb, var(--mar-primary-color) 20%, transparent)', meta: 'colour', description: 'A see through version of any token, for area fills, tracks and hover backgrounds that work on both themes' },
  { label: 'view root', token: '.view { display: flex; flex-direction: column; width: 100%; height: 100vh; min-height: 0; color: var(--mar-text-color); font-family: var(--mar-font-sans); }\n', meta: 'root', description: 'The root every view needs: the frame has no height of its own, so 100vh fills the tile, and min-height 0 lets the children scroll instead of overflowing' },
  { label: 'fill the rest', token: '.plot { flex: 1; min-height: 0; }\n', meta: 'flex', description: 'Gives a chart or list whatever height is left under a header; without min-height 0 a long list pushes the tile open' },
  { label: 'scrolling list', token: '.view { overflow: auto; scrollbar-gutter: stable; }\n', meta: 'scroll', description: 'A list that is the whole view scrolls on its root, so the scrollbar sits on the tile edge and the tile padding stays inside it, the last row included; never overflow on a list inside a padded root' },
  { label: 'list under a chart', token: '.view { --view-padding: 0; }\n.list { flex: 1; min-height: 0; padding: 0 var(--view-inset) var(--view-inset); overflow: auto; scrollbar-gutter: stable; }\n', meta: 'scroll', description: 'A list below a chart or map in an edge to edge view reaches the tile edge and pads its rows inside itself, so the scrollbar sits on the edge and the rows keep the tile inset' },
  { label: 'ranked grid', token: '.bars { display: grid; grid-template-columns: minmax(4.5em, 30%) 1fr minmax(3ch, auto); gap: 0.4em 0.6em; align-content: start; }\n.bar { display: grid; grid-template-columns: subgrid; grid-column: 1 / -1; align-items: center; }\n', meta: 'grid', description: 'Name, bar and value columns shared by every row through subgrid, so all bars start and end at the same place whatever the name length' },
  { label: 'tile grid', token: '.tiles { display: grid; grid-template-columns: repeat(auto-fit, minmax(min(100%, 8em), 1fr)); gap: 0.5em; }\n', meta: 'grid', description: 'As many equal tiles per row as fit, one per row on a narrow tile; nothing is sized in pixels' },
  { label: 'fluid figure', token: '.value { font-family: var(--mar-font-mono); font-size: clamp(1.25em, 14cqi, 2.25em); font-weight: 600; letter-spacing: -0.02em; font-variant-numeric: tabular-nums; }\n', meta: 'type', description: 'A large figure that grows with the tile width between two limits; set container-type: inline-size on its parent for cqi to follow the tile' },
  { label: 'small caps label', token: '.label { font-size: 0.62em; letter-spacing: 0.1em; text-transform: uppercase; color: var(--mar-text-muted-color); }\n', meta: 'type', description: 'The quiet uppercase label over a figure or a column, used across the library' },
  { label: 'ellipsis', token: 'overflow: hidden; text-overflow: ellipsis; white-space: nowrap;', meta: 'text', description: 'Cuts a long name with an ellipsis on one line; give the element a title in the template so the full text shows on hover' },
  { label: 'bar fill', token: '.track { block-size: 0.6em; border-radius: 999px; background: var(--mar-surface-200); }\n.fill { display: block; block-size: 100%; min-inline-size: 1px; border-radius: inherit; background: var(--mar-primary-color); }\n', meta: 'bar', description: 'A pill track with a fill whose inline size comes from the template; min-inline-size keeps a zero visible' },
  { label: 'hatched band', token: 'background: repeating-linear-gradient(135deg, color-mix(in srgb, var(--mar-primary-color) 35%, transparent) 0 0.2em, transparent 0.2em 0.4em);', meta: 'pattern', description: 'Diagonal hatching for a range such as p25 to p75, or for a blocked share, readable without a second colour' },
  { label: 'container query', token: '.meters { container-type: inline-size; }\n@container (min-width: 26em) {\n  .meter { grid-template-columns: 1fr 2fr auto; }\n}\n', meta: 'responsive', description: 'Changes the layout by the width of the tile, not the window, so a view placed small and large both read well' },
  { label: 'sparkline stroke', token: '.spark polyline { fill: none; stroke: var(--mar-primary-color); stroke-width: 1.5; vector-effect: non-scaling-stroke; }\n', meta: 'svg', description: 'Keeps a stretched svg line one stroke wide at any tile size' },
]

export const presentationNotes: Record<string, string> = {
  'Trend lines': 'One line per series over the page range, with annotation marks; reads t in milliseconds, series and value',
  'Stacked area': 'Series stacked into filled bands, so the top edge is the total and each band its part; reads t, series and value',
  'Stacked bars': 'One bar per bucket split into series, for counts that add up such as logs by level; reads t, series and value',
  'Bars with average': 'Faded bars per bucket under a moving average line, so a trend shows through noisy counts; reads t, value and avg',
  'Stat counter': 'One large figure with its change between the two halves of the range and a sparkline under it; reads t and value, with label, unit, agg and good on the rows',
  'KPI tiles': 'A grid of small tiles, each a label, a figure and a note, toned ok, warn or bad; reads label, value, unit, note and tone',
  'Small multiples': 'A small column sparkline per series with its total, to compare many series at once; reads t, series and value',
  'Top list': 'Ranked rows with a bar per row scaled to the largest, the rank and a note under the name; reads label, value, unit and note',
  'Doughnut share': 'A ring of parts of a whole with the total in its centre and a legend; reads label and value',
  'Share strip': 'One full width strip split by share, with a legend of counts and percentages; reads label, value and an optional tone',
  'Treemap': 'Nested rectangles sized by value; a label written as parent / child nests under its parent; reads label and value',
  'Heatmap': 'A grid of series by time bucket in five shades of the accent, with annotation marks; reads t, series and value',
  'Histogram': 'Columns of how many values fall in each range, with gridlines and the peak; reads from, to and count',
  'Percentile band': 'A shaded band between a low and a high line around a solid middle line over time; reads t, low, mid and high, with names for the legend',
  'Distribution': 'A box per series from p25 to p75 with whiskers to min and max and the median marked; reads series, min, p25, p50, p75, max and count',
  'Meters': 'Threshold meters that turn amber past 60 and red past 85 percent, with used over limit; reads label, value, max and unit',
  'Gauge': 'A ring gauge of one figure against its limit, toned by the same thresholds as the meters; reads label, value, max and unit',
  'Table': 'Every column of the rows, with a bar behind each number scaled to its column; reads any flat rows',
  'Log stream': 'Log lines newest first with time, level, service and message, the level as a coloured edge; reads time, severity, service and body',
  'Event timeline': 'One lane per service with events as dots, spans as bars, and annotation marks, over the page range; reads time, end_time, lane, label and severity',
  'Trace waterfall': 'The spans of one trace as bars along its duration, indented by depth and coloured by service; reads trace_id, span_id, service, name, depth, start_ms, duration_ms and failed',
  'Service map': 'Services as nodes and calls as arrows, wider with more calls and red with errors; reads source, target, calls, failed and avg_ms',
  'Request origins': 'Origins on a world map when rows carry lat and lon, a ranked list when they do not; reads name, value, lat and lon',
  'Globe': 'An orthographic globe turned to where the traffic is, with a dot per place sized by value and a ranked list; reads name, value, lat and lon',
}


const themeScale = (name: string, step: number): string => {
  if (step <= 100) return `${name} at step ${step} of its scale, a pale tint: backgrounds, tracks and hover fills behind ${name} marks`
  if (step <= 400) return `${name} at step ${step} of its scale, a soft shade: borders, secondary series and fills that should not compete with the 500`
  if (step === 500) return `${name} itself, step 500 of its scale: marks, series, bars and status dots`
  return `${name} at step ${step} of its scale, a deep shade: text on a pale ${name} tint, hover and pressed states`
}

const surfaceNotes: Record<string, string> = {
  '0': 'The card surface a view sits on: sticky headers, halos around dots, anything that must look cut out of the card',
  '50': 'A step off the card, for a band or a header strip inside the view',
  '100': 'The page background a step under the cards; alternating rows',
  '200': 'Tracks behind bars and meters, quiet separators',
  '300': 'Default borders and scrollbar thumbs',
  '400': 'Stronger borders, disabled text',
  '500': 'Muted labels where text-muted-color is too faint',
}

const familyNotes: Array<[RegExp, (m: RegExpMatchArray) => string]> = [
  [/^--mar-(red|green|amber|blue|orange|purple|yellow|teal)-(\d+)$/, (m) => themeScale(m[1], Number(m[2]))],
  [/^--mar-primary-(\d+)$/, (m) => themeScale('the accent', Number(m[1]))],
  [/^--mar-surface-(\d+)$/, (m) => surfaceNotes[m[1]] ?? `Surface step ${m[1]}, a strong neutral for text and inverted blocks; flips with the theme`],
  [/^--mar-(primary|danger|warn|success|info|help)-contrast-color$/, (m) => `Text and icons placed on a filled ${m[1]} background, so they stay readable in both themes`],
  [/^--mar-(primary|danger|warn|success|info|help)-hover-color$/, (m) => `The ${m[1]} colour under the pointer; a view is read only, so use it for hover on rows and marks`],
  [/^--mar-primary-active-color$/, () => 'The accent while pressed or selected'],
  [/^--mar-primary-color-rgb$/, () => 'The accent as bare r, g, b numbers, for rgba(var(--mar-primary-color-rgb), 0.2) fills'],
  [/^--mar-help-color$/, () => 'The help hue for hints and explanatory marks, apart from the status colours'],
  [/^--mar-code-(\w+)$/, (m) => `The ${m[1]} colour of the code editor palette; use it for code, queries or ids shown inside a view`],
  [/^--mar-sidebar-/, () => 'A colour of the app sidebar; belongs to the shell, rarely right inside a view'],
  [/^--mar-font-(\w[\w-]*)$/, (m) => `The ${m[1]} font family; mono for figures, times and ids, sans for labels and prose`],
  [/^--mar-block-/, () => 'Spacing and colour of the block editor; not meant for views'],
  [/^--mar-paper-/, () => 'The paper surface for document like content, light in both themes; for a printable view'],
  [/^--mar-overlay-/, () => 'Text and scrim colours over an image; for a view drawn over a picture or a map'],
  [/^--mar-highlight-/, () => 'The selection highlight; mark the row or point a reader should look at'],
  [/^--mar-tooltip-/, () => 'Tooltip colours, for a hover card drawn inside a view so it matches the app tooltips'],
  [/^--mar-content-(border-color)$/, () => 'Hairlines between rows, around tables and under headers'],
  [/^--mar-content-/, () => 'The content area colours and radius, for a panel inside the view that should match the app'],
  [/^--mar-image-backdrop$/, () => 'Backdrop behind an image while it loads'],
  [/^--mar-record-card-/, () => 'Record card surfaces; for a view that lists records as cards'],
  [/^--mar-page-header-|^--mar-section-pt$|^--mar-toc-scale$/, () => 'Page layout spacing of the app shell; not meant for views'],
  [/^--glass-blur(-strong)?$/, () => 'The blur radius behind glass, for backdrop-filter: blur(var(--glass-blur))'],
  [/^--glass-saturate$/, () => 'The saturation boost of glass, paired with the blur in backdrop-filter'],
  [/^--glass-shadow$/, () => 'The soft drop shadow of glass cards, for a floating element inside a view'],
  [/^--glass-sheen$/, () => 'The light sheen gradient across glass, for a raised glass tile'],
  [/^--glass-border-strong$/, () => 'A brighter glass edge, for the element in focus'],
  [/^--glass-surface-overlay$/, () => 'Glass for an overlay above other glass'],
]

const collectThemeTokens = (rules: CSSRuleList, names: Set<string>) => {
  for (const rule of Array.from(rules)) {
    if (rule instanceof CSSStyleRule && /:root|html|\[data-theme|\.dark/.test(rule.selectorText)) {
      for (const name of Array.from(rule.style)) if (name.startsWith('--mar-') || name.startsWith('--glass-')) names.add(name)
    }
    if ('cssRules' in rule) collectThemeTokens((rule as CSSGroupingRule).cssRules, names)
  }
}

export const getThemeTokenNames = (): string[] => {
  const names = new Set<string>()
  for (const sheet of Array.from(document.styleSheets)) {
    try {
      collectThemeTokens(sheet.cssRules, names)
    } catch {
      continue
    }
  }
  return [...names].sort()
}

export const getThemeTokenEntries = (names: string[]): MarFuncEntry[] => {
  const known = new Map(styleTokens.map((e) => [e.label ?? e.token, e]))
  return names.map((name) => {
    const group = name.startsWith('--glass-') ? 'glass' : name.split('-').slice(3, 4)[0] || 'theme'
    const entry = known.get(name)
    if (entry) return { ...entry, group }
    const family = familyNotes.find(([re]) => re.test(name))
    const description = family ? family[1](name.match(family[0]) as RegExpMatchArray) : 'A theme variable of Mar; it follows the light and dark theme, so prefer it to a fixed colour'
    return { label: name, token: `var(${name})`, meta: name.startsWith('--glass-') ? 'glass' : 'theme', group, description }
  })
}

const SNAPSHOT_WIDTH = 640

export const getViewSnapshot = async (frame: HTMLElement): Promise<Blob | null> => {
  if (!frame.offsetWidth || !frame.offsetHeight) return null
  try {
    return await domToBlob(frame, { type: 'image/webp', quality: 0.8, scale: SNAPSHOT_WIDTH / frame.offsetWidth })
  } catch {
    return null
  }
}
